import { ButtonHTMLAttributes, useState } from 'react';

/**
 * Button — the primary action control.
 *
 * variant: 'primary' (gold fill, ink text — reserve for THE action on a view),
 *          'inverted' (solid --text fill, --bg label — screen-level CTAs on
 *          the traveler screens: "Open in Maps", "Choose <host>", "Start chatting"),
 *          'secondary' (hairline outline), 'outline' (--text-dim hairline that
 *          warms to --text on hover — "See this day on a map" style),
 *          'ghost' (text only).
 * Always a pill. Gold is for primary actions only — never decorate with it.
 */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual weight. Default 'primary'. */
  variant?: 'primary' | 'inverted' | 'secondary' | 'outline' | 'ghost';
  /** Default 'md'. 'xs' (13px) matches OAuthButton's text size — for a
   *  Button placed directly alongside one where they need to read as a pair. */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Stretch to container width. */
  fullWidth?: boolean;
  /** Shows an inline spinner and disables the button. */
  loading?: boolean;
  /** Tabler icon name (without the `ti-` prefix), e.g. 'arrow-right'. */
  leadingIcon?: string;
  /** 'inverted' — on a 'primary' button, swaps the gold fill for Sekretly's
   *  fixed ink/cream pairing (doesn't track theme). For a fixed dark/violet
   *  backdrop where gold's contrast is too harsh (e.g. auth screens). */
  tone?: 'default' | 'inverted';
}

export function Button({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  leadingIcon,
  tone = 'default',
  children,
  style,
  ...props
}: ButtonProps) {
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  const isDisabled = disabled || loading;

  const sizes = {
    // Fixed height (not just padding-derived) so an xs Button sits flush
    // against an OAuthButton (also a hardcoded 48px) when paired.
    xs: { padding: '0 24px', fontSize: 13, height: 48 },
    sm: { padding: '5px 12px', fontSize: 12 },
    md: { padding: '8px 18px', fontSize: 14 },
    lg: { padding: '12px 24px', fontSize: 16 },
  };

  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: variant === 'outline' ? 7 : 8,
    fontFamily: 'var(--font-sans)',
    // xs always matches OAuthButton's weight (500), regardless of variant.
    fontWeight: size === 'xs' ? 500 : (variant === 'primary' || variant === 'inverted' || variant === 'outline' ? 600 : 500),
    lineHeight: 1,
    borderRadius: 'var(--r-pill)',
    border: '1px solid transparent',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.5 : 1,
    transition: variant === 'outline'
      ? 'border-color .2s ease'
      : 'transform .25s cubic-bezier(.16,.8,.3,1), filter .25s, background .2s, color .2s',
    transform: active && !isDisabled ? 'scale(.97)' : 'scale(1)',
    width: fullWidth ? '100%' : undefined,
    whiteSpace: 'nowrap' as const,
    // 'outline' is a fixed-dimension pill per Sekretly Design System §4.1
    // (second snippet) / §5.9 — it doesn't participate in the sm/md/lg scale.
    ...(variant === 'outline' ? { padding: '9px 18px', fontSize: 12.5 } : sizes[size]),
  };

  const variants = {
    primary: tone === 'inverted' ? {
      background: 'rgb(var(--ink))',
      color: 'rgb(var(--cream-bright))',
      filter: hover && !isDisabled ? 'brightness(1.15)' : 'none',
    } : {
      background: 'rgb(var(--gold))',
      color: 'var(--on-gold)',
      filter: hover && !isDisabled ? 'brightness(1.05)' : 'none',
    },
    inverted: {
      background: 'var(--text)',
      color: 'var(--bg)',
      opacity: isDisabled ? 0.5 : hover ? 0.88 : 1,
    },
    secondary: {
      background: hover && !isDisabled ? 'rgb(var(--text-dim-rgb) / 0.08)' : 'transparent',
      color: 'var(--text)',
      borderColor: 'var(--line)',
    },
    outline: {
      background: 'transparent',
      color: 'var(--text)',
      borderColor: hover && !isDisabled ? 'var(--text)' : 'var(--text-dim)',
    },
    ghost: {
      background: hover && !isDisabled ? 'rgb(var(--text-dim-rgb) / 0.08)' : 'transparent',
      color: hover && !isDisabled ? 'var(--text)' : 'var(--text-soft)',
    },
  };

  return (
    <button
      disabled={isDisabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      style={{ ...base, ...variants[variant], ...style }}
      {...props}
    >
      {loading ? (
        <span aria-hidden="true" style={{ width: '1.1em', height: '1.1em', flexShrink: 0, display: 'inline-block' }}>
          <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" style={{ animation: 'sk-btn-spin .7s linear infinite' }}>
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2.5" />
            <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
          <style>{`@keyframes sk-btn-spin{to{transform:rotate(360deg)}}`}</style>
        </span>
      ) : (leadingIcon ? <i className={`ti ti-${leadingIcon}`} style={{ fontSize: '1.1em' }} /> : null)}
      {children}
    </button>
  );
}

export default Button;
